/**
 * The theme builder's plain-language checks (screenshots/80 "Palette checks" card and the inline problem under a color),
 * built from the dataviz validator port in paletteChecks.ts, so the numbers match the CLI.
 */
import { checkCategorical, checkSequential, contrast, deltaE, isHex, oklch, worstState, type CheckState, type Vision } from "./paletteChecks";
import type { ChartTheme } from "./themes";

export interface Line { state: CheckState | "info"; text: string }
const VISION: Record<Vision, string> = { protan: "protanopia", deutan: "deuteranopia", tritan: "tritanopia" } as Record<Vision, string>;
const floorOf = (detail: string) => Number(/needs (\d+)\+/.exec(detail)?.[1] ?? 15);

/** The problem under color i (vs. the color before it), or null. */
export function pairProblem(palette: string[], i: number): string | null {
  const c = palette[i], prev = palette[i - 1];
  if (!isHex(c)) return "Use a 6-digit hex color.";
  if (i === 0 || !isHex(prev)) return null;
  const k = checkCategorical([prev, c], { surface: "#FFFFFF" });
  const normal = k.find((x) => x.id === "normal")!, cvd = k.find((x) => x.id === "cvd")!;
  if (normal.state !== "fail" && cvd.state !== "fail") return null;
  const d = deltaE(prev, c);
  const worst = (["protan", "deutan"] as Vision[]).map((v) => ({ v, d: deltaE(prev, c, v) })).sort((a, b) => a.d - b.d)[0];
  return `Too close to color ${i}: ΔE ${d.toFixed(1)} (${worst.d.toFixed(1)} with ${VISION[worst.v]}). Needs ${floorOf(normal.detail)}+.`;
}

/** A color for slot i that passes against its neighbours (and the rest of the palette's checks), or null. */
export function suggestColor(palette: string[], i: number): string | null {
  const pool = ["#4F8A10", "#B03A64", "#2E7D6B", "#8A5A00", "#5C6BC0", "#C0582B", "#00838F", "#7B3FA0", "#9E7B00", "#3A6CB3"];
  for (const c of pool) {
    if (palette.includes(c)) continue;
    const next = palette.map((x, j) => (j === i ? c : x));
    if (worstState(checkCategorical(next, { surface: "#FFFFFF" })) !== "fail") return c;
  }
  return null;
}

/** The "Palette checks" card, one line per rule, plus how many problems block a clean save. */
export function themeSummary(t: ChartTheme): { lines: Line[]; problems: number } {
  if (t.palette.some((c) => !isHex(c))) return { lines: [{ state: "fail", text: "Every color needs a 6-digit hex value" }], problems: 1 };
  const cat = checkCategorical(t.palette, { surface: "#FFFFFF" });
  const by = (id: string) => cat.find((c) => c.id === id)!;
  const lines: Line[] = [];
  const basics = [by("band"), by("chroma"), by("contrast")];
  lines.push(basics.every((c) => c.state === "pass") ? { state: "pass", text: "Category colors: lightness band, chroma, 3:1 on white" }
    : { state: worstState(basics), text: basics.filter((c) => c.state !== "pass").map((c) => `${c.label}: ${c.detail}`).join(" · ") });
  let problems = basics.filter((c) => c.state === "fail").length;
  const normal = by("normal"), cvd = by("cvd");
  if (normal.state === "fail" || cvd.state === "fail") {
    problems++;
    const m = /Colors (\d+) and (\d+)/.exec(normal.state === "fail" ? normal.detail : cvd.detail);
    const [a, b] = m ? [Number(m[1]), Number(m[2])] : [1, 2];
    const worst = (["protan", "deutan"] as Vision[]).map((v) => ({ v, d: deltaE(t.palette[a - 1], t.palette[b - 1], v) })).sort((x, y) => x.d - y.d)[0];
    lines.push({ state: "fail", text: `Colors ${a} and ${b} too similar: ΔE ${deltaE(t.palette[a - 1], t.palette[b - 1]).toFixed(1)} (normal), ${worst.d.toFixed(1)} (${VISION[worst.v]})` });
    const without = t.palette.filter((_, j) => j !== b - 1);
    if (worstState(checkCategorical(without, { surface: "#FFFFFF" })) !== "fail") lines.push({ state: "info", text: `Remove color ${b} and all checks pass` });
  } else lines.push({ state: cvd.state, text: `Distinct in normal vision and with color blindness (${normal.detail.replace(/^Colors \d+ and \d+: /, "")})` });
  const seq = checkSequential(t.sequential, { surface: "#FFFFFF" });
  const light = seq.find((c) => c.id === "lightEnd")!.detail.split(" at ")[1];
  lines.push(worstState(seq) === "pass" ? { state: "pass", text: `Sequential: light→dark, even steps, light end ${light}` }
    : { state: "fail", text: `Sequential: ${seq.filter((c) => c.state !== "pass").map((c) => c.detail).join(", ")}` });
  if (worstState(seq) === "fail") problems++;
  const tc = contrast(t.text, "#FFFFFF");
  lines.push({ state: tc >= 4.5 ? "pass" : "fail", text: `Text ${t.text} on white: ${tc.toFixed(1)}:1` });
  if (tc < 4.5) problems++;
  return { lines, problems };
}

/** A 4-step sequential ramp from one color (light → dark), for "From color 1". */
export function rampFrom(hex: string): ChartTheme["sequential"] {
  const mix = (a: string, b: string, t: number) => `#${[1, 3, 5].map((k) => Math.round(parseInt(a.slice(k, k + 2), 16) * (1 - t) + parseInt(b.slice(k, k + 2), 16) * t)
    .toString(16).padStart(2, "0")).join("")}`.toUpperCase();
  const L = oklch(hex).L;
  return [mix(hex, "#FFFFFF", 0.55), mix(hex, "#FFFFFF", 0.22), L > 0.45 ? mix(hex, "#000000", 0.18) : hex, mix(hex, "#000000", 0.45)];
}

/** Five diverging steps from the three stops (negative · middle · positive). */
export const divergingSteps = (d: ChartTheme["diverging"]): string[] => {
  const mix = (a: string, b: string) => `#${[1, 3, 5].map((k) => Math.round((parseInt(a.slice(k, k + 2), 16) + parseInt(b.slice(k, k + 2), 16)) / 2).toString(16).padStart(2, "0")).join("")}`;
  return [d[0], mix(d[0], d[1]), d[1], mix(d[1], d[2]), d[2]];
};
